<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue'

// 当全局 Loading 显示时，锁定页面滚动
onMounted(() => {
  const prev = document.documentElement.style.overflow
  document.documentElement?.setAttribute('data-prev-overflow', prev || '')
  document.documentElement.style.overflow = 'hidden'
})

onUnmounted(() => {
  const prev = document.documentElement?.getAttribute('data-prev-overflow')
  document.documentElement.style.overflow = prev ?? ''
  document.documentElement?.removeAttribute('data-prev-overflow')
})
</script>

<template>
  <!-- 全局遮罩层 -->
  <div
    class="fixed inset-0 z-[9999] flex items-center justify-center bg-white/5 backdrop-blur-xs"
    role="status"
    aria-live="polite"
    aria-busy="true"
  >
    <!-- 背景轻柔光晕（浅、扁平） -->
    <div
      class="pointer-events-none absolute aspect-square w-[12rem] rounded-full opacity-30 blur-3xl"
      style="
        background: radial-gradient(
          50% 50% at 50% 50%,
          rgba(147, 197, 253, 0.22),
          rgba(96, 165, 250, 0.18),
          transparent 70%
        );
      "
    />

    <!-- 动效容器 -->
    <div class="relative flex flex-col items-center justify-center gap-5">
      <div class="relative grid place-items-center">
        <!-- 直接嵌入 SVG，针对每条 path 单独做扫光动效（乳白色光带，自左向右循环） -->
        <svg
          class="h-24 w-24"
          viewBox="0 0 1024 1024"
          fill="none"
          aria-hidden="true"
        >
          <defs>
            <!-- 基础色渐变，贴合当前 H5 蓝色体系 -->
            <linearGradient id="baseGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stop-color="#93c5fd" />
              <stop offset="100%" stop-color="#60a5fa" />
            </linearGradient>

            <!-- 轻微色彩流动渐变（更淡，更细腻） -->
            <linearGradient
              id="flowGrad"
              x1="0"
              y1="0"
              x2="1"
              y2="0"
              gradientUnits="objectBoundingBox"
            >
              <stop offset="0%" stop-color="#bfdbfe" />
              <stop offset="50%" stop-color="#93c5fd" />
              <stop offset="100%" stop-color="#bfdbfe" />
            </linearGradient>

            <!-- 乳白色光带（边缘柔和） -->
            <linearGradient
              id="shineBand"
              x1="0"
              y1="0"
              x2="1"
              y2="0"
              gradientUnits="objectBoundingBox"
            >
              <stop offset="0" stop-color="#ffffff" stop-opacity="0" />
              <stop offset="0.42" stop-color="#ffffff" stop-opacity="0" />
              <stop offset="0.5" stop-color="#ffffff" stop-opacity="0.9" />
              <stop offset="0.58" stop-color="#ffffff" stop-opacity="0" />
              <stop offset="1" stop-color="#ffffff" stop-opacity="0" />
            </linearGradient>

            <!-- 柔光滤镜 -->
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="16" result="b" />
              <feBlend in="SourceGraphic" in2="b" mode="screen" />
            </filter>

            <!-- 路径定义（来自 favicon.svg） -->
            <path
              id="logo-path-1"
              d="M0 0 C1.24555161 -0.00101212 1.24555161 -0.00101212 2.51626587 -0.00204468 C35.37792999 0.03998143 67.6568714 4.71307895 99.125 14.25 C99.90101563 14.48299805 100.67703125 14.71599609 101.4765625 14.95605469 C127.45267179 22.86716357 152.77907805 34.80502453 175.125 50.25 C175.69476562 50.63865234 176.26453125 51.02730469 176.8515625 51.42773438 C185.74291534 57.50871468 194.0615051 64.11186553 202.125 71.25 C202.87861816 71.91684814 202.87861816 71.91684814 203.64746094 72.59716797 C215.37271767 83.0369948 226.38432362 93.91543773 236.125 106.25 C237.56208578 107.9586818 238.999596 109.6670067 240.4375 111.375 C250.12280353 123.24076453 258.53101782 135.96632685 266.125 149.25 C266.51896973 149.9311084 266.91293945 150.6122168 267.31884766 151.31396484 C278.95917628 171.48961173 288.17245292 192.36664092 296.125 214.25 C296.45628906 215.15717773 296.78757812 216.06435547 297.12890625 216.99902344 C308.13627257 247.49956045 327.82588007 302.35021508 314.125 334.25 C311.18055422 339.24913691 307.67816316 342.1337149 302.1171875 343.8125 C290.80177857 346.29745255 280.30319998 339.52925396 270.875 334 C268.83256094 332.82850312 266.78959314 331.65792756 264.74609375 330.48828125 C262.18443508 329.0132842 259.62322829 327.53751654 257.06298828 326.06005859 C198.43380263 292.51856073 124.76871551 278.04375196 58 278 C56.92696625 277.99899292 55.8539325 277.99798584 54.74838257 277.99694824 C34.74390419 278.0095489 15.00179732 279.03282184 -4.875 281.25 C-6.1837207 281.39453613 -7.49244141 281.53907227 -8.84082031 281.68798828 C-41.73745121 285.45105614 -74.53057396 291.87617216 -106.375 300.9375 C-107.34477783 301.20353027 -108.31455566 301.46956055 -109.3137207 301.74365234 C-132.84592014 308.67949841 -154.83289539 320.47667965 -176.62329102 331.54510498 C-182.94904708 334.75718172 -189.28133762 337.95632268 -195.6171875 341.1484375 C-196.30190313 341.49361294 -196.98661877 341.83878838 -197.69208336 342.19442368 C-211.60871857 349.20058252 -225.69424871 355.79505602 -239.875 362.25 C-241.04463791 362.78448257 -242.21427582 363.31896515 -243.4193573 363.86964417 C-334.98848093 405.69490333 -436.81003505 427.95663258 -551.92871094 390.3671875 C-599.48065637 372.31777105 -640.79478912 339.07574507 -666.875 295.25 C-667.2781543 294.57388672 -667.68130859 293.89777344 -668.09667969 293.20117188 C-680.04814376 272.93604524 -699.10045236 239.25792707 -693.33984375 214.99609375 C-692.36610354 212.44734205 -691.23083599 210.56629866 -689.1875 208.75 C-683.64112525 207.55078384 -679.36438616 208.65036285 -674.6875 211.625 C-671.49104447 213.90044292 -668.55909712 216.39151594 -665.62670898 218.99536133 C-631.84196004 248.793647 -585.35063257 259.61847152 -541.875 265.25 C-540.87065918 265.38277344 -539.86631836 265.51554687 -538.83154297 265.65234375 C-475.53857954 273.58764749 -415.39426709 263.16384587 -364.14990234 223.92919922 C-360.98426007 221.45409395 -357.92416669 218.86623157 -354.875 216.25 C-353.84626572 215.39177232 -352.81624923 214.53507995 -351.78515625 213.6796875 C-341.37694804 205.01371267 -331.34741892 195.93554835 -321.875 186.25 C-320.71732579 185.09232579 -319.55952727 183.93477541 -318.40087891 182.77807617 C-316.57871134 180.95328468 -314.76418259 179.12101878 -312.94921875 177.2890625 C-311.4870367 175.81765116 -310.0247957 174.3462984 -308.5625 172.875 C-307.84126953 172.14539063 -307.12003906 171.41578125 -306.37695312 170.6640625 C-305.65830078 169.9421875 -304.93964844 169.2203125 -304.19921875 168.4765625 C-303.56072998 167.83283691 -302.92224121 167.18911133 -302.2644043 166.52587891 C-300.27065488 164.62685652 -298.24098981 162.8281136 -296.15551758 161.03271484 C-291.58311291 157.0556362 -287.32278746 152.806512 -283.0625 148.5 C-281.84276489 147.27458496 -281.84276489 147.27458496 -280.59838867 146.02441406 C-278.02146703 143.43537329 -275.44782375 140.84311263 -272.875 138.25 C-269.48212847 134.83037281 -266.08585343 131.41417851 -262.6875 128 C-261.92268311 127.22688477 -261.15786621 126.45376953 -260.36987305 125.65722656 C-256.26278427 121.53166377 -252.07470408 117.57449513 -247.65278625 113.78720093 C-244.70611735 111.2393006 -241.89570654 108.54746594 -239.0625 105.875 C-234.06609017 101.19992632 -228.96285388 96.7452831 -223.6171875 92.46875 C-221.20294479 90.51534564 -218.84681125 88.50891693 -216.50097656 86.47436523 C-195.71706364 68.53671896 -172.6793856 52.72311007 -148.74414062 39.29980469 C-146.98277313 38.31053089 -145.23314096 37.30044558 -143.484375 36.2890625 C-108.66167926 16.81061397 -69.4551388 5.38399303 -29.875 1.25 C-28.99553711 1.1557373 -28.11607422 1.06147461 -27.20996094 0.96435547 C-18.14739301 0.09540599 -9.09654504 0.00669824 0 0 Z M-190.984375 147.7578125 C-193.64918199 150.05530826 -196.36913717 152.25096212 -199.125 154.4375 C-210.64506077 163.81547041 -221.79875334 174.33200301 -231.875 185.25 C-231.875 185.58 -231.875 185.91 -231.875 186.25 C-222.34706991 187.32949479 -212.82265502 187.52592748 -203.24682617 187.71484375 C-201.48421532 187.75244627 -199.72161171 187.79038919 -197.95901489 187.8286438 C-193.19080451 187.93119917 -188.42250844 188.02908763 -183.6541841 188.12617874 C-178.61772776 188.22960124 -173.58136874 188.33756384 -168.54499817 188.44506836 C-158.97526022 188.64869439 -149.40544852 188.84859707 -139.83560115 189.04700756 C-125.93057945 189.33542233 -112.02565689 189.62854517 -98.1207552 189.92268372 C-96.56869909 189.95551468 -96.56869909 189.95551468 -94.98528832 189.9890089 C-92.91488722 190.03280973 -90.84448638 190.07662335 -88.77408582 190.12045002 C-82.8314026 190.24611164 -76.88869297 190.37041223 -70.94595724 190.49356461 C-59.25541625 190.73598335 -47.5651134 190.98777214 -35.875 191.25 C-34.99644112 191.26952602 -34.11788224 191.28905205 -33.21270037 191.30916977 C-27.99026142 191.42560036 -22.76797557 191.54694488 -17.5458113 191.67512321 C-15.95223519 191.71371118 -14.35862657 191.75098543 -12.76498449 191.78674507 C25.98262757 192.66267773 64.2618618 199.37383035 102.25292969 206.62182617 C103.56767525 206.87263443 103.56767525 206.87263443 104.90898132 207.12850952 C106.53875529 207.44049047 108.16819002 207.75425157 109.79719543 208.07022095 C114.22852117 208.92249852 118.65248087 209.64851947 123.125 210.25 C114.36644684 201.30673734 104.43090779 194.17134791 94 187.3125 C93.40662842 186.92059479 92.81325684 186.52868958 92.2019043 186.12490845 C63.67465568 167.29812909 30.5526308 146.01654283 -4.875 147.25 C10.33711457 130.32333055 44.95097483 128.75319605 66.34716797 127.42919922 C81.59228504 126.70605614 96.86628849 126.51135935 112.125 126.25 C103.38982733 107.50151691 73.64300505 95.00394129 55.32421875 88.09765625 C-34.8050288 55.43279636 -121.2096376 87.12289693 -190.984375 147.7578125 Z"
              transform="translate(703.875,484.75)"
            />
            <path
              id="logo-path-2"
              d="M0 0 C1.02738281 0.81984375 2.05476562 1.6396875 3.11328125 2.484375 C16.75116203 13.59087744 29.30042721 26.10237399 39.72607422 40.28173828 C41.02250666 42.03035682 42.35288718 43.74828803 43.69140625 45.46484375 C69.19217308 78.59039439 84.63816494 119.72862741 90 161 C90.14139404 162.00426025 90.14139404 162.00426025 90.28564453 163.02880859 C91.3042882 171.37658435 91.22182999 179.78805643 91.24023438 188.18505859 C91.24995269 190.86196834 91.28106106 193.53811282 91.3125 196.21484375 C91.45369152 218.73070742 91.45369152 218.73070742 85 228 C82.5625 228.625 82.5625 228.625 80 228 C76.43076848 224.30601865 74.96420203 220.53981016 73.625 215.6875 C73.20011857 214.25156271 72.77162631 212.81668974 72.33984375 211.3828125 C72.12054199 210.65352539 71.90124023 209.92423828 71.67529297 209.17285156 C64.31258511 185.4822876 52.55191581 163.32046879 37 144 C36.18273437 142.9790625 35.36546875 141.958125 34.5234375 140.90625 C23.49314314 127.3811802 11.15152648 115.22782037 -3 105 C-3.56058105 104.58975586 -4.12116211 104.17951172 -4.69873047 103.75683594 C-33.04756919 83.23256921 -67.1965188 70.43245342 -102 67 C-103.0828125 66.88914062 -104.165625 66.77828125 -105.28125 66.6640625 C-160.09767365 61.74122834 -213.52363572 80.31897956 -255.5 115.25 C-297.87603054 151.40233707 -324.03624187 203.55322759 -328.53515625 259.05957031 C-330.48004651 287.12879388 -328.19973477 316.56228747 -318 343 C-313.1015274 355.92362983 -313.1015274 355.92362983 -313 361 C-314.4375 363.9375 -314.4375 363.9375 -316 366 C-326.16301352 364.94197367 -334.55526763 356.50877824 -341 349 C-353.08649601 334.04146533 -362.87679384 317.39439901 -371 300 C-371.2985791 299.3694873 -371.5971582 298.73897461 -371.90478516 298.08935547 C-377.24594946 286.72882998 -381.2671809 274.97537761 -385 263 C-385.21188965 262.32855957 -385.4237793 261.65711914 -385.64208984 260.96533203 C-386.61155451 257.76975217 -387 255.39287092 -387 252 C-413.63517552 251.39189097 -437.01194091 253.68496236 -461 266 C-462.14210938 266.58523438 -463.28421875 267.17046875 -464.4609375 267.7734375 C-473.27260885 272.46130026 -480.68274657 278.24417267 -488 285 C-488.9590625 285.8765625 -489.918125 286.753125 -490.90625 287.65625 C-513.98167432 309.6945766 -527.17599895 341.14814721 -528.20703125 372.9140625 C-528.3504798 381.90440706 -527.8428686 390.82733517 -527.0234375 399.77734375 C-527.00047758 402.93433256 -527.29354981 404.40124571 -529 407 C-532.4965757 407.50991729 -534.77253032 407.69092623 -537.89453125 405.93359375 C-559.5902125 388.30309107 -570.31933551 355.90523725 -573.6875 329.1875 C-576.49519036 292.86673963 -566.96305269 256.78478868 -543.1875 228.75 C-524.66549746 207.63551171 -502.00963512 193.63302317 -475 186 C-473.81019531 185.65839844 -472.62039063 185.31679688 -471.39453125 184.96484375 C-446.01149041 179.01353101 -417.95719547 182.32192766 -394 192 C-394.03480469 191.27941406 -394.06960938 190.55882812 -394.10546875 189.81640625 C-395.58270483 131.21937521 -370.12022875 73.64533385 -332.5 29.91015625 C-331.06814976 28.24339309 -329.64533478 26.56854342 -328.25 24.87109375 C-325.12865096 21.07967249 -321.72513979 17.90996845 -317.98046875 14.734375 C-316.16821441 13.14731203 -314.52837624 11.49916378 -312.875 9.75 C-295.81974943 -7.5784468 -272.94012066 -20.91104832 -251 -31 C-249.92443848 -31.4964502 -249.92443848 -31.4964502 -248.82714844 -32.00292969 C-167.09557626 -69.21839501 -69.79805722 -55.83844578 0 0 Z"
              transform="translate(600,168)"
            />

            <!-- clipPath，按路径裁切扫光矩形 -->
            <clipPath id="clip-1"><use href="#logo-path-1" /></clipPath>
            <clipPath id="clip-2"><use href="#logo-path-2" /></clipPath>
          </defs>

          <!-- 基础 logo 填充 -->
          <use href="#logo-path-1" fill="url(#baseGrad)" />
          <use href="#logo-path-2" fill="url(#baseGrad)" />

          <!-- 轻微色彩流动（path1）-->
          <g clip-path="url(#clip-1)">
            <rect
              class="flow flow1"
              x="-800"
              y="-300"
              width="2200"
              height="1700"
              fill="url(#flowGrad)"
              opacity="0.18"
            />
          </g>
          <!-- 轻微色彩流动（path2）-->
          <g clip-path="url(#clip-2)">
            <rect
              class="flow flow2"
              x="-800"
              y="-300"
              width="2200"
              height="1700"
              fill="url(#flowGrad)"
              opacity="0.16"
            />
          </g>
          <!-- 乳白光带（path1）-->
          <g clip-path="url(#clip-1)">
            <!-- 统一方向：全部旋转 15deg，自左向右扫过 -->
            <g transform="rotate(15 512 512)">
              <rect
                class="sweep speed-fast delay-0"
                x="-760"
                y="-360"
                width="520"
                height="1900"
                fill="url(#shineBand)"
                filter="url(#glow)"
              />
            </g>
            <g transform="rotate(15 512 512)">
              <rect
                class="sweep speed-med delay-120"
                x="-740"
                y="-360"
                width="560"
                height="1900"
                fill="url(#shineBand)"
                filter="url(#glow)"
              />
            </g>
            <g transform="rotate(15 512 512)">
              <rect
                class="sweep speed-slow delay-240"
                x="-720"
                y="-360"
                width="600"
                height="1900"
                fill="url(#shineBand)"
                filter="url(#glow)"
              />
            </g>
          </g>
          <!-- 乳白光带（path2）-->
          <g clip-path="url(#clip-2)">
            <g transform="rotate(15 512 512)">
              <rect
                class="sweep speed-fast delay-60"
                x="-760"
                y="-360"
                width="520"
                height="1900"
                fill="url(#shineBand)"
                filter="url(#glow)"
              />
            </g>
            <g transform="rotate(15 512 512)">
              <rect
                class="sweep speed-med delay-180"
                x="-740"
                y="-360"
                width="560"
                height="1900"
                fill="url(#shineBand)"
                filter="url(#glow)"
              />
            </g>
            <g transform="rotate(15 512 512)">
              <rect
                class="sweep speed-slow delay-300"
                x="-720"
                y="-360"
                width="600"
                height="1900"
                fill="url(#shineBand)"
                filter="url(#glow)"
              />
            </g>
          </g>
        </svg>
      </div>

      <!-- 文案 -->
      <div
        class="select-none text-center text-sm font-medium tracking-wide text-slate-600"
      >
        <span class="inline-block text-breathe">
          <span
            class="bg-gradient-to-r from-blue-500 via-blue-600 to-indigo-500 bg-clip-text text-transparent [animation:shine_2.2s_linear_infinite]"
          >
            正在加载，请稍候…
          </span>
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* 文字高光扫过 */
@keyframes shine {
  0% {
    filter: brightness(1);
  }
  50% {
    filter: brightness(1.25);
  }
  100% {
    filter: brightness(1);
  }
}

/* 轻微色彩流动（宽幅 rect 水平位移） */
@keyframes flow-move {
  0% {
    transform: translateX(0%);
  }
  100% {
    transform: translateX(-20%);
  }
}

/* flow 层动画（极轻） */
.flow {
  animation: flow-move 9s ease-in-out infinite alternate;
  will-change: transform;
}
.flow1 {
  animation-duration: 10s;
  animation-delay: 0.2s;
}
.flow2 {
  animation-duration: 11s;
  animation-delay: 0.6s;
}

/* 乳白光带自左向右扫过（SVG rect 平移） */
@keyframes sweep {
  0% {
    transform: translateX(-70%);
    opacity: 0;
  }
  15% {
    opacity: 0.9;
  }
  50% {
    transform: translateX(60%);
    opacity: 0.9;
  }
  85% {
    opacity: 0;
  }
  100% {
    transform: translateX(140%);
    opacity: 0;
  }
}

.sweep {
  animation: sweep 2.4s cubic-bezier(0.4, 0, 0.2, 1) infinite;
  will-change: transform, opacity;
}
/* 速度档位 */
.speed-fast {
  animation-duration: 1.9s;
}
.speed-med {
  animation-duration: 2.3s;
}
.speed-slow {
  animation-duration: 2.7s;
}

/* 错峰延时 */
.delay-0 {
  animation-delay: 0s;
}
.delay-60 {
  animation-delay: 0.06s;
}
.delay-120 {
  animation-delay: 0.12s;
}
.delay-180 {
  animation-delay: 0.18s;
}
.delay-240 {
  animation-delay: 0.24s;
}
.delay-300 {
  animation-delay: 0.3s;
}

/* 文字轻微呼吸（缩放+透明度） */
@keyframes breathe {
  0%,
  100% {
    transform: translateZ(0) scale(1);
    opacity: 0.9;
  }
  50% {
    transform: translateZ(0) scale(1.02);
    opacity: 1;
  }
}
.text-breathe {
  animation: breathe 2.6s cubic-bezier(0.4, 0, 0.2, 1) infinite;
  display: inline-block;
}

/* 动画可访问性：用户偏好减少动效时，弱化动画 */
@media (prefers-reduced-motion: reduce) {
  .sweep {
    animation: none;
    opacity: 0.12;
  }
  .flow {
    animation: none;
    opacity: 0.1;
  }
  .text-breathe {
    animation: none;
  }
}
</style>
